import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Send, Mail, Users, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface Post {
  id: number;
  title: string;
  slug: string;
  category: string;
  created_at: string;
}

interface NewsletterHistory {
  id: string;
  post_id: number | null;
  subject: string;
  sent_at: string;
  recipients_count: number;
  status: string;
  posts?: { title: string } | null;
}

const NewsletterManager = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [history, setHistory] = useState<NewsletterHistory[]>([]);
  const [subscribersCount, setSubscribersCount] = useState(0);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [customSubject, setCustomSubject] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      // Load published posts
      const { data: postsData, error: postsError } = await supabase
        .from('posts')
        .select('id, title, slug, category, created_at')
        .eq('status', 'published')
        .order('created_at', { ascending: false });

      if (postsError) throw postsError;
      setPosts(postsData || []);

      // Load newsletter history with post titles
      const { data: historyData, error: historyError } = await supabase
        .from('newsletter_history')
        .select('*, posts(title)')
        .order('sent_at', { ascending: false })
        .limit(10);

      if (historyError) throw historyError;
      setHistory(historyData || []);

      // Count active subscribers
      const { count, error: countError } = await supabase
        .from('newsletter_subscribers')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'active');

      if (countError) throw countError;
      setSubscribersCount(count || 0);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendNewsletter = async () => {
    if (!selectedPost) {
      toast.error('Selecione um artigo para enviar');
      return;
    }

    if (subscribersCount === 0) {
      toast.error('Não há subscribers ativos');
      return;
    }

    setIsSending(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-newsletter', {
        body: {
          postId: selectedPost.id,
          customSubject: customSubject || undefined,
        },
      });

      if (error) throw error;

      if (data.success) {
        toast.success(`Newsletter enviada para ${data.sent} subscribers!`);
        if (data.errors > 0) {
          toast.warning(`${data.errors} emails falharam`);
        }
        setSelectedPost(null);
        setCustomSubject('');
        loadData(); // Reload history
      } else {
        throw new Error(data.error || 'Erro ao enviar newsletter');
      }
    } catch (error: any) {
      console.error('Error sending newsletter:', error);
      toast.error(error.message || 'Erro ao enviar newsletter');
    } finally {
      setIsSending(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-60 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Subscribers Ativos</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{subscribersCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Newsletters Enviadas</CardTitle>
            <Mail className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{history.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Artigos Disponíveis</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{posts.length}</div>
          </CardContent>
        </Card>
      </div>

      {/* Send Newsletter Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="h-5 w-5" />
            Enviar Newsletter
          </CardTitle>
          <CardDescription>
            Selecione um artigo para enviar aos {subscribersCount} subscribers ativos
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Post Selection */}
          <div className="space-y-2">
            <Label>Selecione o Artigo</Label>
            <div className="grid gap-2 max-h-60 overflow-y-auto border rounded-lg p-2">
              {posts.map((post) => (
                <div
                  key={post.id}
                  onClick={() => setSelectedPost(post)}
                  className={`p-3 rounded-lg cursor-pointer transition-colors ${
                    selectedPost?.id === post.id
                      ? 'bg-primary/10 border-2 border-primary'
                      : 'bg-muted/50 hover:bg-muted border-2 border-transparent'
                  }`}
                >
                  <div className="font-medium text-sm">{post.title}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className="text-xs">
                      {post.category}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {format(new Date(post.created_at), 'dd/MM/yyyy', { locale: ptBR })}
                    </span>
                  </div>
                </div>
              ))}
              {posts.length === 0 && (
                <p className="text-muted-foreground text-center py-4">
                  Nenhum artigo publicado disponível
                </p>
              )}
            </div>
          </div>

          {/* Custom Subject */}
          <div className="space-y-2">
            <Label htmlFor="subject">Assunto Personalizado (opcional)</Label>
            <Input
              id="subject"
              placeholder={selectedPost ? `📖 ${selectedPost.title} - KopfAI Blog` : 'Selecione um artigo primeiro'}
              value={customSubject}
              onChange={(e) => setCustomSubject(e.target.value)}
              disabled={!selectedPost}
            />
          </div>

          {/* Send Button */}
          <Button
            onClick={handleSendNewsletter}
            disabled={!selectedPost || isSending || subscribersCount === 0}
            className="w-full"
          >
            {isSending ? (
              <>
                <Clock className="mr-2 h-4 w-4 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" />
                Enviar Newsletter para {subscribersCount} subscribers
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* History Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Histórico de Envios
          </CardTitle>
          <CardDescription>
            Últimas newsletters enviadas
          </CardDescription>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              Nenhuma newsletter enviada ainda
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Assunto</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead>Destinatários</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-medium max-w-xs truncate">
                      {item.subject}
                    </TableCell>
                    <TableCell>
                      {format(new Date(item.sent_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                    </TableCell>
                    <TableCell>{item.recipients_count}</TableCell>
                    <TableCell>
                      <Badge
                        variant={item.status === 'sent' ? 'default' : 'secondary'}
                        className="flex items-center gap-1 w-fit"
                      >
                        {item.status === 'sent' ? (
                          <CheckCircle className="h-3 w-3" />
                        ) : (
                          <AlertCircle className="h-3 w-3" />
                        )}
                        {item.status === 'sent' ? 'Enviado' : 'Parcial'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default NewsletterManager;
